// src/pages/ResourceMatrix.tsx
// HP8: Wired to live Roster allocation store — dynamic weeks, live conflict detection
// Replaced static RESOURCE_MATRIX_CREW with getRosterAllocations() from Roster.tsx

import { useMemo, useState } from 'react'
import { MOCK_EMPLOYEES, MOCK_EQUIPMENT, JOB_COLOUR } from '../utils/mockDataExtended'
import { MOCK_ALLOCATIONS, MOCK_EQUIPMENT_ALLOCATIONS } from '../utils/mockDataRoster'
import { getRosterAllocations } from './Roster'
import { MOCK_JOBS } from '../utils/mockData'
import {
  AlertTriangle, CheckCircle, ChevronLeft, ChevronRight,
  RefreshCw, Truck, Users,
} from 'lucide-react'

// ── Dynamic date helpers (mirrors HP5 fix in Roster.tsx) ──────────

function getThisMonday(): string {
  const now  = new Date()
  const day  = now.getDay()
  const diff = day === 0 ? -6 : 1 - day
  const d    = new Date(now)
  d.setDate(now.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d.toISOString().slice(0, 10)
}

function datePlusDays(base: string, n: number): string {
  const d = new Date(base + 'T00:00:00')
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

function shortDate(iso: string) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

function dayAbbr(iso: string) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short' })
}

// Module-level Monday anchor (recalculated once per page load, not per render)
const BASE_MONDAY = getThisMonday()

// ── Cell components ───────────────────────────────────────────────

function Cell({ jobId }: { jobId: string | null }) {
  if (!jobId) {
    return (
      <td className="border border-gray-100 p-0">
        <div className="h-9 bg-gray-50 flex items-center justify-center text-xs text-gray-300">—</div>
      </td>
    )
  }
  if (jobId === 'SVC') {
    return (
      <td className="border border-gray-100 p-0">
        <div className="h-9 bg-orange-100 flex items-center justify-center text-xs font-medium text-orange-700">Svc</div>
      </td>
    )
  }
  const cfg = JOB_COLOUR[jobId] ?? JOB_COLOUR['null']
  const job = MOCK_JOBS.find(j => j.id === jobId)
  return (
    <td className="border border-gray-100 p-0">
      <div className={`h-9 flex items-center justify-center text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
        {job?.jobNumber?.split('-').pop() ?? jobId}
      </div>
    </td>
  )
}

function WeekendCell() {
  return (
    <td className="border border-gray-100 p-0">
      <div className="h-9 bg-gray-100 flex items-center justify-center text-xs text-gray-300">—</div>
    </td>
  )
}

function UtilBar({ value, label }: { value: number; label: string }) {
  const color = value >= 95 ? 'bg-red-500' : value >= 80 ? 'bg-amber-500' : 'bg-green-500'
  const text  = value >= 95 ? 'text-red-700' : value >= 80 ? 'text-amber-700' : 'text-green-700'
  return (
    <div className="flex-1">
      <div className="flex justify-between mb-1">
        <span className="text-xs text-gray-500">{label}</span>
        <span className={`text-xs font-bold ${text}`}>{value}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────

export function ResourceMatrix() {
  const [weekOffset, setWeekOffset] = useState(0)

  // Dynamic week dates
  const weekStart = datePlusDays(BASE_MONDAY, weekOffset * 7)
  const weekDates = Array.from({ length: 7 }, (_, i) => datePlusDays(weekStart, i))
  const weekLabel = `${shortDate(weekDates[0])} \u2013 ${shortDate(weekDates[4])}`

  // HP8: Pull live allocations from Roster store; fall back to seed if store empty
  const rosterAllocs = getRosterAllocations()
  const allocations  = rosterAllocs.length > 0 ? rosterAllocs : MOCK_ALLOCATIONS
  const isLive       = rosterAllocs.length > 0

  // Build crew matrix rows from live allocations
  const crewRows = useMemo(() => {
    return MOCK_EMPLOYEES.map(emp => {
      const dayJobs = weekDates.map(date => {
        const alloc = allocations.find(a => a.employeeId === emp.id && a.date === date)
        return alloc?.jobId ?? null
      })
      const allocatedDays = dayJobs.slice(0, 5).filter(Boolean).length
      return {
        id:    emp.id,
        name:  emp.fullName,
        trade: (emp as any).trade ?? '\u2014',
        week:  dayJobs,
        util:  Math.round(allocatedDays / 5 * 100),
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterAllocs.length, weekStart])

  // Build equipment matrix rows from equipment allocations
  const equipRows = useMemo(() => {
    return MOCK_EQUIPMENT.slice(0, 12).map(eq => {
      const dayJobs = weekDates.map(date => {
        const alloc = MOCK_EQUIPMENT_ALLOCATIONS.find(a => a.equipmentId === eq.id && a.date === date)
        return alloc?.jobId ?? null
      })
      const allocatedDays = dayJobs.slice(0, 5).filter(Boolean).length
      return {
        id:       eq.id,
        name:     eq.name,
        category: eq.category,
        week:     dayJobs,
        util:     Math.round(allocatedDays / 5 * 100),
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart])

  // Live conflict detection: employee allocated to >1 job on same workday
  const conflicts = useMemo(() => {
    const seen: { name: string; date: string }[] = []
    const dedupe = new Set<string>()
    weekDates.slice(0, 5).forEach(date => {
      MOCK_EMPLOYEES.forEach(emp => {
        const sameDayAllocs = allocations.filter(a => a.employeeId === emp.id && a.date === date)
        if (sameDayAllocs.length > 1) {
          const key = `${emp.id}:${date}`
          if (!dedupe.has(key)) {
            dedupe.add(key)
            seen.push({ name: emp.fullName, date })
          }
        }
      })
    })
    return seen
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterAllocs.length, weekStart])

  // Portfolio summary stats
  const crewUtil         = Math.round(crewRows.reduce((s, r) => s + r.util, 0) / Math.max(crewRows.length, 1))
  const plantUtil        = Math.round(equipRows.reduce((s, r) => s + r.util, 0) / Math.max(equipRows.length, 1))
  const allocatedCount   = crewRows.filter(r => r.util > 0).length
  const activeJobIds     = [...new Set(allocations.filter(a => weekDates.includes(a.date)).map(a => a.jobId))]

  return (
    <div className="space-y-5">

      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Resource Matrix</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Cross-project crew and plant allocation \u2014 {isLive ? 'live from Roster' : 'seed data'}
          </p>
          {!isLive && (
            <p className="text-xs text-amber-600 mt-0.5">
              Visit Roster once to load live allocations into the shared store.
            </p>
          )}
        </div>
        <button
          onClick={() => setWeekOffset(0)}
          className="btn-secondary text-xs flex items-center gap-1.5"
        >
          <RefreshCw className="w-3.5 h-3.5" />This week
        </button>
      </div>

      {/* Portfolio utilisation */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900">Portfolio utilisation</h2>
          <span className="text-xs text-gray-400">
            {allocatedCount} of {crewRows.length} crew on {activeJobIds.length} job{activeJobIds.length !== 1 ? 's' : ''} this week
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <UtilBar value={crewUtil}  label="Crew capacity" />
          <UtilBar value={plantUtil} label="Plant & fleet" />
        </div>

        {conflicts.length > 0 ? (
          <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              <strong>{conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''}:</strong>{' '}
              {conflicts.slice(0, 3).map(c => `${c.name} (${shortDate(c.date)})`).join(', ')}
              {conflicts.length > 3 ? ` +${conflicts.length - 3} more` : ''} \u2014 resolve in Roster
            </span>
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">
            <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
            No allocation conflicts detected for this week
          </div>
        )}

        {crewUtil >= 85 && (
          <div className="mt-2 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            Crew at {crewUtil}% capacity \u2014 limited buffer for additional work or emergency callouts
          </div>
        )}
      </div>

      {/* Week navigator */}
      <div className="flex items-center gap-3">
        <button onClick={() => setWeekOffset(w => w - 1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-semibold text-gray-900 flex-1 text-center">
          {weekLabel}
          <span className="text-gray-400 font-normal ml-2 text-xs">
            {weekOffset === 0 ? '(this week)' : weekOffset > 0 ? `(+${weekOffset}w)` : `(${weekOffset}w)`}
          </span>
        </span>
        <button onClick={() => setWeekOffset(w => w + 1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Job legend */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs text-gray-500 mr-1">Jobs:</span>
        {MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status)).map(j => {
          const cfg = JOB_COLOUR[j.id]
          if (!cfg) return null
          return (
            <span key={j.id} className={`text-xs px-2 py-0.5 rounded font-medium ${cfg.bg} ${cfg.text}`}>
              {j.jobNumber}
            </span>
          )
        })}
        <span className="text-xs px-2 py-0.5 rounded font-medium bg-orange-100 text-orange-700">Service</span>
        <span className="text-xs px-2 py-0.5 rounded font-medium bg-gray-100 text-gray-400">Available</span>
      </div>

      {/* Crew matrix */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-500" />
          <h2 className="font-semibold text-gray-900">Crew allocation</h2>
          <span className="text-xs text-gray-400 ml-1">{crewRows.length} people</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm" style={{ minWidth: 560 }}>
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 w-44 sticky left-0 bg-gray-50">Name</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-28">Trade</th>
                {weekDates.map((date, i) => (
                  <th
                    key={date}
                    className={`text-center py-2.5 text-xs font-medium text-gray-500 ${i >= 5 ? 'bg-gray-100' : ''}`}
                    style={{ minWidth: 80 }}
                  >
                    <div>{dayAbbr(date)}</div>
                    <div className="font-normal text-gray-400">{date.slice(8)}/{date.slice(5,7)}</div>
                  </th>
                ))}
                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-16">Util</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {crewRows.map(row => (
                <tr key={row.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-1 font-medium text-gray-900 text-sm sticky left-0 bg-white">{row.name}</td>
                  <td className="px-3 py-1 text-xs text-gray-500">{row.trade}</td>
                  {row.week.map((jobId, i) => (
                    i >= 5 ? <WeekendCell key={i} /> : <Cell key={i} jobId={jobId} />
                  ))}
                  <td className="px-4 py-1 text-right">
                    <span className={`text-xs font-semibold ${
                      row.util >= 100 ? 'text-red-600' :
                      row.util >= 80  ? 'text-amber-600' :
                      row.util > 0    ? 'text-green-600' : 'text-gray-400'
                    }`}>{row.util}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Plant & equipment matrix */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b flex items-center gap-2">
          <Truck className="w-4 h-4 text-gray-500" />
          <h2 className="font-semibold text-gray-900">Plant &amp; equipment allocation</h2>
          <span className="text-xs text-gray-400 ml-1">{equipRows.length} items</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm" style={{ minWidth: 560 }}>
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 w-52 sticky left-0 bg-gray-50">Asset</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-24">Category</th>
                {weekDates.map((date, i) => (
                  <th
                    key={date}
                    className={`text-center py-2.5 text-xs font-medium text-gray-500 ${i >= 5 ? 'bg-gray-100' : ''}`}
                    style={{ minWidth: 80 }}
                  >
                    <div>{dayAbbr(date)}</div>
                    <div className="font-normal text-gray-400">{date.slice(8)}/{date.slice(5,7)}</div>
                  </th>
                ))}
                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-16">Util</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {equipRows.map(row => (
                <tr key={row.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-1 font-medium text-gray-900 text-xs sticky left-0 bg-white">{row.name}</td>
                  <td className="px-3 py-1 text-xs text-gray-500">{row.category}</td>
                  {row.week.map((jobId, i) => (
                    i >= 5 ? <WeekendCell key={i} /> : <Cell key={i} jobId={jobId} />
                  ))}
                  <td className="px-4 py-1 text-right">
                    <span className={`text-xs font-semibold ${
                      row.util >= 100 ? 'text-red-600' :
                      row.util >= 80  ? 'text-amber-600' :
                      row.util > 0    ? 'text-green-600' : 'text-gray-400'
                    }`}>{row.util}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap gap-6 text-sm">
        <div>
          <span className="text-xs text-gray-500">Crew tracked</span>
          <div className="font-bold">{crewRows.length} people</div>
        </div>
        <div>
          <span className="text-xs text-gray-500">Active this week</span>
          <div className="font-bold">{allocatedCount} allocated</div>
        </div>
        <div>
          <span className="text-xs text-gray-500">Active jobs</span>
          <div className="font-bold">{activeJobIds.length}</div>
        </div>
        <div>
          <span className="text-xs text-gray-500">Plant items</span>
          <div className="font-bold">{equipRows.length}</div>
        </div>
        <div>
          <span className="text-xs text-gray-500">Conflicts</span>
          <div className={`font-bold ${conflicts.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {conflicts.length === 0 ? 'None' : conflicts.length}
          </div>
        </div>
        <div className="ml-auto text-xs text-gray-400 self-center">
          {isLive ? 'Live from Roster store' : 'Seed data — visit Roster to activate'} \u00b7 week nav across {weekOffset === 0 ? 'current' : `offset ${weekOffset}`} week
        </div>
      </div>
    </div>
  )
}
